import { api } from "@/lib/api";
import {
  ServiceProvider,
  ServiceProviderDomain,
  ServiceProviderDTOSchema,
  serviceProviderFromDto,
  serviceProviderToDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getAllServiceProvidersQueryOptions,
  getServiceProviderQueryOptions,
  getServiceProvidersQueryOptions,
} from "./get.hooks";

const toastDomainMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.serviceProviderProviderUpdated",
  provider: "toast.serviceProviderServiceUpdated",
};

const toastDomainErrorMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.updateServiceProviderProviderError",
  provider: "toast.updateServiceProviderServiceError",
};

export function useUpdateServiceProvider(
  domain: ServiceProviderDomain,
  domainId: string,
  opts?: {
    onSuccess?: (serviceProvider: ServiceProvider) => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (serviceProvider: ServiceProvider) => {
      const dto = serviceProviderToDto(serviceProvider);
      const result = await api.put(
        "/api/service-providers",
        dto,
        ServiceProviderDTOSchema,
      );
      return serviceProviderFromDto(result);
    },
    onSuccess: (serviceProvider) => {
      queryClient.setQueryData(
        getServiceProvidersQueryOptions(domain, domainId).queryKey,
        (old) =>
          old?.map((sp) =>
            sp.id !== serviceProvider.id ? sp : serviceProvider,
          ),
      );
      queryClient.setQueryData(
        getAllServiceProvidersQueryOptions.queryKey,
        (old) =>
          old?.map((sp) =>
            sp.id !== serviceProvider.id ? sp : serviceProvider,
          ),
      );
      queryClient.setQueriesData(
        getServiceProviderQueryOptions(serviceProvider.id),
        () => serviceProvider,
      );
      toast({
        title: toastDomainMessageMap[domain],
        variant: "success",
      });
      opts?.onSuccess?.(serviceProvider);
    },
    onError: () => {
      toast({
        title: toastDomainErrorMessageMap[domain],
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
